import { useEffect, useState } from 'react'
import { fetchAll } from '../lib/fetchall'
import { MANUAL_TRIGGER } from '../lib/outbox'
import type { OutreachDraft } from '../lib/types'
import { formatDateTime, fullName, triggerLabel } from '../lib/format'

type DraftWithAgent = OutreachDraft & {
  agents: {
    rezen_id: string
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
}

/**
 * Review only, deliberately — and this is the page where that matters most,
 * because it is the one that looks like it should have a Send button.
 *
 * Two standing rules, both about not corrupting history rather than about the
 * UI being unfinished:
 *
 *   1. Nothing sends email from this build. A sending path gets designed and
 *      switched on as its own deliberate step.
 *   2. Drive stays the record until cutover. Renee is still working in the old
 *      dashboard; a "sent" marked here would split the history in two, and the
 *      cutover re-import is what carries her interim work across. It cannot do
 *      that if this app has invented its own version of events.
 *
 * **This tab is the milestone lane only.** One-off sends written by the composer
 * land in the same table tagged `manual`, and they used to be listed here beside
 * the birthdays. Four test cards made that look cosmetic; one reviewed batch to
 * 386 people would put 386 manual cards at the top, newest first, and bury the
 * entire milestone programme underneath them — at which point the banner above
 * claiming to be the record of that programme stops being true. A one-off's
 * record lives on the agent's own card, which is where "what have we sent this
 * person" belongs.
 */
export default function Drafts() {
  const [drafts, setDrafts] = useState<DraftWithAgent[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * Separate from `error`, because an incomplete read still has rows worth
   * showing. Putting it in `error` would replace the whole page with a sentence
   * and hide the history it is warning about.
   */
  const [warning, setWarning] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    /**
     * Paged, because a plain `.select()` here was capped at `db.max_rows` and
     * said nothing about it — the failure `fetchAll` exists to prevent. Eleven
     * rows made it invisible; one 785-person reviewed batch reaches the cap on
     * its own and the history silently stops.
     *
     * The sort ends in `id` because `created_at` defaults to `now()`, which is
     * *transaction* time: every row of one batch insert shares it exactly, and
     * a tie block straddling a page boundary is a row dropped or repeated.
     *
     * `manual` is filtered here rather than in the query because `fetchAll`
     * takes an equality filter and this needs a *not*-equal. Widening that
     * helper to carry operators would put a new expression into the one place
     * every sendable read goes through, to save fetching rows on a review
     * screen. Not worth it.
     */
    fetchAll<DraftWithAgent>(
      'outreach_drafts',
      '*, agents(rezen_id, first_name, last_name, email)',
      ['created_at', 'id'],
    ).then((r) => {
      if (cancelled) return
      if (r.error) return setError(r.error)
      // Newest first. `fetchAll` sorts ascending because that is what makes the
      // paging sound; the display order is a separate decision.
      setDrafts(r.rows.filter((d) => d.trigger !== MANUAL_TRIGGER).reverse())
      /**
       * An incomplete answer says so, and says *which end* is missing.
       *
       * `fetchAll` pages upwards from the oldest row, so a truncated read is
       * always short at the recent end — and this list is displayed newest
       * first. "There is more than this" would leave her assuming the gap was
       * in the distant past, which is the opposite of true.
       */
      if (r.truncated) {
        setWarning(
          'Only part of the history could be read, and it is the most recent that is missing. ' +
            'Do not treat this list as complete.',
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (error) return <div className="empty">Could not load drafts: {error}</div>
  if (!drafts) return <div className="empty">Loading…</div>

  const pending = drafts.filter((d) => d.status === 'pending')

  return (
    <div className="board">
      <div className="notice">
        <strong>Review only.</strong> {pending.length} pending. Nothing here can be sent or
        marked sent, on purpose — the existing dashboard is still the record until cutover,
        and marking something here would split the history in two.
      </div>
      <div className="hint" style={{ marginTop: -8, marginBottom: 14 }}>
        Milestone emails only — birthdays, anniversaries and the like. One-off emails you
        wrote yourself are on the agent's own record, not here.
      </div>

      {warning && <p className="error">{warning}</p>}

      {drafts.length === 0 && <div className="hint">No milestone drafts.</div>}

      {drafts.map((d) => (
        <section className="sec" key={d.id} style={{ marginBottom: 14 }}>
          <h3>{d.agents ? fullName(d.agents) : 'Unknown agent'}</h3>
          <div className="emeta">
            <span>
              {triggerLabel(d.trigger)} · {d.occurrence}
              {d.agents?.email && <span className="hint"> · {d.agents.email}</span>}
            </span>
            <span className={`pill s-${d.status}`}>{d.status}</span>
          </div>
          {d.why && <div className="hint">{d.why}</div>}
          <div className="dsubject">{d.subject}</div>
          <div className="dbody">{d.body}</div>
          <div className="hint">Generated {formatDateTime(d.created_at)}</div>
        </section>
      ))}
    </div>
  )
}
